import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  useTSMApproveComplaint,
  useTSMPostProcessComplaint,
} from "../../hooks/useTSQualityRecords";
import { paginationDefaults } from "../../config/settingsDefaults";
import { ComplaintFull } from "../../types/qualityRecordTypes";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { ApproveIcon } from "../../components/common/NucleusSymbolsAndIcons";

interface CFData extends Omit<ComplaintFull, "customer_id" | "rma_addr"> {
  customer_code: string;
  customer_name: string;
  reported_to_name: string;
  reported_to_email: string;
  submitted_by_name: string;
  submitted_by_email: string;
}

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageComplaintList: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const [targetReportedTo, setTargetReportedTo] = useState<string>("");
  const [targetCust, setTargetCust] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [orderBy, setOrderBy] = useState<string>("date_reported");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const approveComplaintMutation = useTSMApproveComplaint();
  const postProcComplaintMutation = useTSMPostProcessComplaint();

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "cf_number":
        return [
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "cf_number",
            asc: order === "asc",
          },
        ];
      case "cf_category":
        return [
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "cf_category",
            asc: order === "asc",
          },
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "date_reported",
            asc: false,
          },
        ];
      case "customer":
        return [
          {
            table: "customers",
            alt_name: "cust",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "date_reported",
            asc: false,
          },
        ];
      case "date_of_occurrence":
        return [
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "date_of_occurrence",
            asc: order === "asc",
          },
        ];
      case "date_reported":
        return [
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "date_reported",
            asc: order === "asc",
          },
        ];
      case "reported_to":
        return [
          {
            table: "employees",
            alt_name: "rep_to",
            field: "first_name",
            asc: order === "asc",
          },
          {
            table: "customer_feedback",
            alt_name: "cf",
            field: "date_reported",
            asc: false,
          },
        ];
      default:
        return [];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetCust !== "") {
      filts[0] = [];
      filts[1] = [];
      filts[0].push({
        table: "customers",
        alt_name: "cust",
        field: "name",
        operator: "contains",
        value: targetCust,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "customers",
        alt_name: "cust",
        field: "code",
        operator: "contains",
        value: targetCust,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetReportedTo !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "employees",
        alt_name: "rep_to",
        field: "first_name",
        operator: "contains",
        value: targetReportedTo,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "employees",
        alt_name: "rep_to",
        field: "last_name",
        operator: "contains",
        value: targetReportedTo,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    return filts;
  }, [targetReportedTo, targetCust]);

  const {
    data: cfResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "customer_feedback",
    alt_name: "cf",
    primary_fields: [],
    joins: [
      {
        table: "customers",
        alt_name: "cust",
        fields: ["id", "name", "code"],
        condition: {
          remote_table: "customer_feedback",
          alt_name: "cf",
          remote_field: "customer_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "employees",
        alt_name: "rep_to",
        fields: ["id", "first_name", "last_name", "email"],
        condition: {
          remote_table: "customer_feedback",
          alt_name: "cf",
          remote_field: "reported_to",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "employees",
        alt_name: "sub_by",
        fields: ["id", "first_name", "last_name", "email"],
        condition: {
          remote_table: "customer_feedback",
          alt_name: "cf",
          remote_field: "submitted_by",
          local_field: "id",
        },
        outer: false,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [],
  });

  const [feedbackObjs, recCount]: [CFData[], number] = useMemo(() => {
    const feedbackObjs: CFData[] = [];
    if (cfResponse?.items) {
      cfResponse.items.forEach((val) => {
        console.log("CF Value", val);
        feedbackObjs.push({
          cf_label: val["cf.cf_label"],
          cf_number: val["cf.cf_number"],
          date_of_occurrence: val["cf.date_of_occurrence"],
          cf_category: val["cf.cf_category"],
          cat_other: val["cf.cat_other"],
          issue_summary: val["cf.issue_summary"],
          nature_of_issue: val["cf.nature_of_issue"],
          contact_name: val["cf.contact_name"],
          contact_email: val["cf.contact_email"],
          contact_phone: val["cf.contact_phone"],
          date_reported: val["cf.date_reported"],
          product_data: val["cf.product_data"],
          to_be_returned: val["cf.to_be_returned"],
          box_file_id: val["cf.box_file_id"],
          gl_cf_id: val["cf.gl_cf_id"],
          gl_cf_display_id: val["cf.gl_cf_display_id"],
          gl_cf_approved_id: val["cf.gl_cf_approved_id"],
          gl_cf_num: val["cf.gl_cf_num"],
          customer_code: val["cust.code"],
          customer_name: val["cust.name"],
          reported_to: val["cf.reported_to"],
          submitted_by: val["cf.submitted_by"],
          reported_to_name: `${val["rep_to.first_name"]} ${val["rep_to.last_name"]}`,
          reported_to_email: val["rep_to.email"],
          submitted_by_name: `${val["sub_by.first_name"]} ${val["sub_by.last_name"]}`,
          submitted_by_email: val["sub_by.email"],
          approved: val["cf.approved"],
          id: val["cf.id"],
          created_at: val["cf.created_at"],
          updated_at: val["cf.updated_at"],
          is_record_active: val["cf.is_record_active"],
        });
      });
    }

    return [feedbackObjs, cfResponse?.total || 0];
  }, [cfResponse]);

  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);
  const [processDialogError, setProcessDialogError] = useState<string | null>(
    null,
  );

  const handleProcessDialogClose = (
    event: any,
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick") {
      return;
    }
    setDisplayProcessDialog(false);
    setProcessDialogError(null);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const approveComplaint = async (id: string) => {
    if (!id) {
      setError("Missing ID, Cannot Approve Complaint");
      return;
    }
    setDisplayProcessDialog(true);
    try {
      const appResult = await approveComplaintMutation.mutateAsync({ id });
      if (appResult.success) {
        const procResult = await postProcComplaintMutation.mutateAsync({ id });
        if (!procResult.success) {
          setProcessDialogError(
            `Complaint Post Processing failed: ${procResult.message || "UNKNOWN REASON"}`,
          );
        } else {
          setTimeout(() => {
            handleProcessDialogClose(null, "escapeKeyDown");
          }, 5000);
        }
      } else {
        setProcessDialogError(
          `Complaint Approval failed: ${appResult.message || "UNKNOWN REASON"}`,
        );
      }
    } catch (e) {
      setError(
        "There was an error completing the Complaint Approval Process. Notify IT so it can be fixed. If you want to be extra helpful, you can send a screenshot of the browser console.",
      );
      console.error(e);
      return;
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Customer Complaints
        </Typography>
        <Box display="flex" gap={0.5}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              navigate("/quality/customer_complaint/form?mode=create")
            }
          >
            Create New Complaint
          </Button>
          {user?.is_superuser && (
            <Button
              variant="outlined"
              color="info"
              onClick={() => console.log(feedbackObjs)}
            >
              DEBUG PRINT
            </Button>
          )}
        </Box>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap={"wrap"} gap={1}>
          <FilterBox
            finalSet={setTargetReportedTo}
            placeholder="Filter By Employee Reported To (First or Last Name)"
          />
          <FilterBox
            finalSet={setTargetCust}
            placeholder="Filter By Customer Name/Code"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "cf_number" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "cf_number"}
                  direction={orderBy === "cf_number" ? order : "asc"}
                  onClick={() => handleSort("cf_number")}
                >
                  NUC CF Number
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%">Greenlight Info</TableCell>
              <TableCell
                sortDirection={orderBy === "customer" ? order : false}
                width="20%"
              >
                <TableSortLabel
                  active={orderBy === "customer"}
                  direction={orderBy === "customer" ? order : "asc"}
                  onClick={() => handleSort("customer")}
                >
                  Customer / Contact
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "date_of_occurrence" ? order : false}
                width="12%"
              >
                <TableSortLabel
                  active={orderBy === "date_of_occurrence"}
                  direction={orderBy === "date_of_occurrence" ? order : "asc"}
                  onClick={() => handleSort("date_of_occurrence")}
                >
                  Date of Occurrence
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "date_reported" ? order : false}
                width="12%"
              >
                <TableSortLabel
                  active={orderBy === "date_reported"}
                  direction={orderBy === "date_reported" ? order : "asc"}
                  onClick={() => handleSort("date_reported")}
                >
                  Date Reported
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "reported_to" ? order : false}
                width="12%"
              >
                <TableSortLabel
                  active={orderBy === "reported_to"}
                  direction={orderBy === "reported_to" ? order : "asc"}
                  onClick={() => handleSort("reported_to")}
                >
                  Reported To
                </TableSortLabel>
              </TableCell>
              <TableCell width="8%">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : feedbackObjs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {filters.length > 0
                      ? "No CF Records found matching your search."
                      : "No Customer Complaint Records found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              feedbackObjs.map((cf) => (
                <TableRow key={cf.id} hover>
                  <TableCell>
                    <Stack direction="column" gap={0.5}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        NUC-CF-{cf.cf_number.toString().padStart(4, "0")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {cf.cf_label}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {cf.gl_cf_display_id && cf.gl_cf_id ? (
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        <a
                          href={`https://centese.greenlight.guru/${cf.gl_cf_num ? "grow" : "growRequests"}/customerFeedback/${cf.gl_cf_num ? cf.gl_cf_approved_id : cf.gl_cf_display_id}/source`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View in Greenlight
                        </a>
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Not in Greenlight Yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" gap={0.5}>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Contact: {cf.contact_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Contact Email: {cf.contact_email}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Contact Phone: {cf.contact_phone}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        Customer: {cf.customer_code} {cf.customer_name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {new Date(cf.date_of_occurrence).toLocaleDateString(
                        "en-US",
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {new Date(cf.date_reported).toLocaleDateString("en-US")}
                    </Typography>
                  </TableCell>
                  <TableCell>{cf.reported_to_name}</TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={1}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/quality/customer_complaint/form?mode=view&id=${cf.id}`,
                              )
                            }
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!cf.approved && (
                          <Tooltip title="Edit Complaint">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/quality/customer_complaint/form?mode=edit&id=${cf.id}`,
                                )
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!cf.approved &&
                          ([
                            cf.submitted_by_email,
                            cf.reported_to_email,
                          ].includes(user?.user_id || "") ||
                            user?.is_superuser) && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => approveComplaint(cf.id)}
                                color="success"
                                disabled={approveComplaintMutation.isPending}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
      <Dialog
        open={displayProcessDialog}
        onClose={(e, r) => {
          handleProcessDialogClose(e, r);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Stack
            direction="column"
            width="100%"
            justifyContent={"center"}
            gap={3}
          >
            {!!processDialogError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {processDialogError}
              </Alert>
            )}
            <Box
              display="flex"
              gap={2}
              justifyContent="space-evenly"
              width="50%"
            >
              <Typography variant="h5">Approval</Typography>
              {approveComplaintMutation.isPending ? (
                <CircularProgress />
              ) : approveComplaintMutation.data?.success ? (
                <CheckIcon color="primary" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            <Box
              display="flex"
              gap={2}
              justifyContent="space-evenly"
              width="50%"
            >
              <Typography variant="h5">Post Processing</Typography>
              {approveComplaintMutation.isPending ||
              postProcComplaintMutation.isPending ? (
                <CircularProgress />
              ) : postProcComplaintMutation.data?.success ? (
                <CheckIcon color="primary" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            {approveComplaintMutation.isPending ||
            postProcComplaintMutation.isPending ? (
              <Typography variant="h5">
                Page will return to Complaint List View upon request
                completion...
              </Typography>
            ) : !(
                !!approveComplaintMutation.data?.success &&
                !!postProcComplaintMutation.data?.success
              ) ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  handleProcessDialogClose(null, "escapeKeyDown");
                }}
              >
                Close
              </Button>
            ) : (
              <Typography variant="h5">
                Requests completed, returning to Complaint List View mode...
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
